import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';

const TYPE_CONFIG = {
  alerte:     { icon: '!',  color: '#EF4444', bg: '#FEF2F2' },
  urgence:    { icon: '!!', color: '#991B1B', bg: '#FEE2E2' },
  validation: { icon: 'V',  color: '#F59E0B', bg: '#FFFBEB' },
  info:       { icon: 'i',  color: '#1565c0', bg: '#EFF6FF' },
  default:    { icon: 'N',  color: '#666',    bg: '#F5F5F5' },
};

const MODULE_LABELS = {
  incidents: 'Incidents',
  audit: 'Audit',
  facturation: 'Facturation',
  devis: 'Devis',
  formations: 'Formations',
  rondes: 'Rondes',
  telesurveillance: 'TSV',
  validations: 'Validations',
  agents: 'Agents',
  contrats: 'Contrats',
  controleur: 'Controleur',
  prises_service: 'Prises de service',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "A l'instant";
  if (mins < 60) return `Il y a ${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Il y a ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Il y a ${days}j`;
  return new Date(dateStr).toLocaleDateString('fr-FR');
}

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [nonLues, setNonLues] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchCount = useCallback(() => {
    api.get('/notifications/count').then(res => setNonLues(res.data.non_lues || 0)).catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data || []);
    } catch {}
    setLoading(false);
  }, []);

  // Poll count every 30s
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Fetch full list when opening
  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (notif) => {
    if (!notif.lu) {
      await api.put(`/notifications/${notif.id}/lire`).catch(() => {});
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, lu: true } : n));
      setNonLues(prev => Math.max(0, prev - 1));
    }
    if (notif.lien) {
      setOpen(false);
      navigate(notif.lien);
    }
  };

  const markAllRead = async () => {
    await api.put('/notifications/lire-tout').catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, lu: true })));
    setNonLues(0);
  };

  const deleteNotif = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/notifications/${id}`).catch(() => {});
    setNotifications(prev => prev.filter(n => n.id !== id));
    fetchCount();
  };

  return (
    <div className="notif-container" ref={ref}>
      <button className="notif-bell" onClick={() => setOpen(!open)} title="Notifications">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {nonLues > 0 && <span className="notif-badge">{nonLues > 99 ? '99+' : nonLues}</span>}
      </button>
      {open && (
        <div className="notif-dropdown">
          <div className="notif-header">
            <span style={{ fontWeight: 700, fontSize: 14 }}>Notifications {nonLues > 0 ? `(${nonLues})` : ''}</span>
            {nonLues > 0 && <button className="notif-mark-all" onClick={markAllRead}>Tout marquer lu</button>}
          </div>
          <div className="notif-list">
            {loading && notifications.length === 0 && (
              <div className="notif-empty">Chargement...</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="notif-empty">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#CCC" strokeWidth="1.5" style={{ marginBottom: 8 }}>
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
                </svg>
                <div>Aucune notification</div>
              </div>
            )}
            {notifications.slice(0, 30).map(n => {
              const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.default;
              return (
                <button
                  key={n.id}
                  className={`notif-item ${n.lu ? '' : 'notif-unread'}`}
                  onClick={() => markRead(n)}
                >
                  <span className="notif-type-icon" style={{ background: cfg.bg, color: cfg.color, fontWeight: 800 }}>
                    {cfg.icon}
                  </span>
                  <div className="notif-content">
                    <div className="notif-title">{n.titre}</div>
                    {n.message && <div className="notif-desc">{n.message}</div>}
                    <div className="notif-meta">
                      {n.module && <span className="notif-module">{MODULE_LABELS[n.module] || n.module}</span>}
                      <span className="notif-date">{timeAgo(n.created_at)}</span>
                      {n.emetteur_nom && <span className="notif-emetteur">par {n.emetteur_nom}</span>}
                    </div>
                  </div>
                  <div className="notif-actions-col">
                    {!n.lu && <span className="notif-dot" />}
                    <button className="notif-delete" onClick={(e) => deleteNotif(e, n.id)} title="Supprimer">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function useAlertCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get('/notifications/count').then(res => setCount(res.data.non_lues || 0)).catch(() => {});
  }, []);
  return count;
}
